import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { toObservable } from '@angular/core/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, first, map, shareReplay, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { CommonModule } from '@angular/common';
import { User } from 'src/app/models/user.model';
import { ApiService, UserService, PermissionsService } from 'src/app/services';
import { UtilsService } from 'src/app/services/utils.service';
import { Document, DocumentWithProcessedComment } from 'src/app/models/document.model';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { DocumentType } from 'src/app/models/document_type.model';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { CreateDocumentDialogComponent } from 'src/app/components/create-document-dialog/create-document-dialog.component';
import { EditDocumentDialogComponent } from 'src/app/components/edit-document-dialog/edit-document-dialog.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { Observable } from 'rxjs';

interface RouteContext {
  userId: string;
  isViewingOwnDocuments: boolean;
  me: User | null;
}

interface DocumentsResult {
  data: DocumentWithProcessedComment[];
  meta: { total: number; current_page: number; per_page: number };
}

@Component({
  selector: 'app-user-documents-list',
  templateUrl: './user-documents-list.component.html',
  styleUrls: ['./user-documents-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatTableModule,
    TypeSafeMatCellDeferred,
  ],
})
export class UserDocumentsListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly userService = inject(UserService);
  private readonly permissions = inject(PermissionsService);
  private readonly dialog = inject(MatDialog);
  private readonly appUtils = inject(UtilsService);
  private readonly destroy_ref = inject(DestroyRef);

  readonly dataSource = new MatTableDataSource<DocumentWithProcessedComment>([]);
  readonly displayedColumns: string[] = ['type', 'size', 'start_date', 'end_date', 'comment', 'actions'];
  readonly COMMENT_MAX_LENGTH = 30;

  readonly documentTypeOptions = computed(() =>
    this.documentTypes().map(type => ({
      value: type.id,
      label: type.name,
      related_user_can_view_document: type.related_user_can_view_document,
    }))
  );

  override filterFormExtraParams: { search: string[]; document_type_ids: number[][] } = {
    search: [''],
    document_type_ids: [[]],
  };

  override serializer: { document_type_ids: () => string } = {
    document_type_ids: (): string => {
      const value = this.filterForm.get('document_type_ids')?.value;
      return Array.isArray(value) ? value.join(',') : '';
    },
  };

  override deserializer: { document_type_ids: (value: string) => number[] } = {
    document_type_ids: (value: string): number[] => {
      if (!value || value === '') return [];
      return value
        .split(',')
        .map(s => Number(s.trim()))
        .filter(v => Number.isFinite(v));
    },
  };

  private relatedTypesFilterSetup = false;

  private readonly routeContext$ = this.activatedRoute.paramMap.pipe(
    first(),
    map(() => {
      const paramMap = this.activatedRoute.snapshot.paramMap;
      const parentParamMap = this.activatedRoute.parent?.snapshot.paramMap;
      const me = this.userService.getCurrentUser();
      const id = paramMap.get('id') ?? parentParamMap?.get('id') ?? null;
      const userId = id ?? String(me?.id ?? '');
      const isViewingOwnDocuments = this.activatedRoute.snapshot.routeConfig?.path === 'my-documents';

      return {
        userId,
        isViewingOwnDocuments,
        me: me ?? null,
      } satisfies RouteContext;
    }),
    shareReplay(1)
  );

  protected readonly routeContext = toSignal<RouteContext | null>(this.routeContext$, { initialValue: null });

  readonly user = toSignal<User | null>(
    this.routeContext$.pipe(switchMap(ctx => (ctx.isViewingOwnDocuments && ctx.me ? of(ctx.me) : this.api.getUser(ctx.userId)))),
    { initialValue: null }
  );

  readonly documentTypes = toSignal(
    this.routeContext$.pipe(
      switchMap(ctx =>
        this.api.getDocumentTypes({
          user_id: ctx.userId,
          should_filter_only_existing_document_types: false,
          per_page: 1000,
        })
      ),
      map((res: ArrayResponse<DocumentType>) => res.data)
    ),
    { initialValue: [] as DocumentType[] }
  );

  readonly isViewingOwnDocuments = computed(() => this.routeContext()?.isViewingOwnDocuments ?? false);

  readonly is_admin_or_rrhh = toSignal(
    this.permissions.current_departments_observable.pipe(
      map((): boolean => {
        return this.permissions.validateDepartment(['direction', 'rrhh']);
      })
    ),
    { initialValue: false }
  );

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
    this.setupDocumentTypesReadySubscription();
  }

  override ngOnInit(): void {
    this.processUrlParams();
    const urlPage = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (!urlPage) {
      this.pageIndex = 0;
    }
    super.ngOnInit();
    this.setupSearchListener();
  }

  override fetchData(): void {
    this.loadDocuments$()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: DocumentsResult | null) => {
        if (!result) return;
        this.dataSource.data = result.data;
        this.setMetadata(result.meta.total, result.meta.current_page - 1, result.meta.per_page);
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchData();
  }

  override changePage(event: PageEvent): void {
    this.onPageChange(event);
    super.changePage(event);
  }

  createDocument(): void {
    const userId = this.routeContext()?.userId;
    if (!userId) return;
    this.api
      .getDocumentTypes({
        user_id: userId,
        should_filter_only_existing_document_types: false,
        per_page: 1000,
      })
      .pipe(
        takeUntilDestroyed(this.destroy_ref),
        switchMap((response: ArrayResponse<DocumentType>) =>
          this.dialog
            .open(CreateDocumentDialogComponent, {
              width: '600px',
              data: { types: response.data, userId },
            })
            .afterClosed()
        )
      )
      .subscribe((result: Document | undefined) => {
        if (result) this.fetchData();
      });
  }

  editDocument(doc: DocumentWithProcessedComment): void {
    const currentUser = this.user();
    if (!currentUser) return;
    this.dialog
      .open(EditDocumentDialogComponent, {
        width: '600px',
        data: { document: doc, user: currentUser },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: Document | undefined) => {
        if (!result) return;
        const index = this.dataSource.data.findIndex(d => d.id === doc.id);
        if (index !== -1) {
          const updatedDoc = {
            ...this.dataSource.data[index],
            ...result,
          } as DocumentWithProcessedComment;
          updatedDoc.truncatedComment = this.getTruncatedComment(updatedDoc.comment);
          updatedDoc.shouldShowTooltip = this.shouldShowTooltip(updatedDoc.comment);
          this.dataSource.data[index] = updatedDoc;
          this.dataSource.data = [...this.dataSource.data];
        }
      });
  }

  downloadDocument(doc: DocumentWithProcessedComment): void {
    this.api
      .downloadDocument(doc.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((blob: Blob) => {
        const filename = doc.document_type?.name ?? `document-${doc.id}`;
        this.appUtils.downloadDocumentFromBlob(blob, filename);
      });
  }

  deleteDocument(doc: DocumentWithProcessedComment): void {
    if (!confirm('¿Seguro que quieres eliminar el documento?')) return;
    this.api
      .deleteDocument(doc.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => {
        this.dataSource.data = this.dataSource.data.filter(d => d.id !== doc.id);
      });
  }

  isActive(doc: DocumentWithProcessedComment): boolean {
    const today = new Date();
    const startDate = doc.start_date ? new Date(doc.start_date) : null;
    const endDate = doc.end_date ? new Date(doc.end_date) : null;
    if (startDate && today < startDate) return false;
    if (endDate && today > endDate) return false;
    return true;
  }

  protected getTruncatedComment(comment: string | null): string {
    if (!comment) return '-';
    return comment.length > this.COMMENT_MAX_LENGTH ? comment.substring(0, this.COMMENT_MAX_LENGTH).trim() + '...' : comment;
  }

  private processUrlParams(): void {
    const urlPerPage = this.activatedRoute.snapshot.queryParamMap.get('per_page');
    if (urlPerPage) {
      this.pageSize = parseInt(urlPerPage, 10);
    }
    const urlPage = this.activatedRoute.snapshot.queryParamMap.get('page');
    if (urlPage) {
      this.pageIndex = parseInt(urlPage, 10) - 1;
    }
  }

  private setupSearchListener(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((searchTerm: string) => {
        if (searchTerm !== '') {
          this.pageIndex = 0;
          this.fetchData();
        }
      });
  }

  private setupRelatedTypesFilter(): void {
    if (this.relatedTypesFilterSetup) return;
    const documentTypesControl = this.filterForm.get('document_type_ids');
    if (documentTypesControl) {
      if (!Array.isArray(documentTypesControl.value)) {
        documentTypesControl.setValue([]);
      }
      documentTypesControl.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(() => {
        this.pageIndex = 0;
        this.fetchData();
      });
    }
    this.relatedTypesFilterSetup = true;
  }

  private setupDocumentTypesReadySubscription(): void {
    toObservable(computed(() => this.documentTypes()))
      .pipe(
        filter((types: DocumentType[]) => types.length > 0),
        first(),
        takeUntilDestroyed(this.destroy_ref)
      )
      .subscribe(() => {
        this.setupRelatedTypesFilter();
        this.fetchData();
      });
  }

  private loadDocuments$(): Observable<DocumentsResult | null> {
    const ctx = this.routeContext();
    if (!ctx) return of(null);
    const documentTypeIds = this.filterForm.get('document_type_ids')?.value ?? [];
    const searchTerm = this.filterForm.get('search')?.value ?? '';
    const params: {
      page: number;
      per_page: number;
      document_type_ids?: string;
      search?: string;
    } = {
      page: this.pageIndex + 1,
      per_page: this.pageSize,
    };
    if (Array.isArray(documentTypeIds) && documentTypeIds.length > 0) {
      params.document_type_ids = documentTypeIds.join(',');
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    return this.api.getDocumentsByUser(ctx.userId, params).pipe(
      map((res: ArrayResponse<Document>) => {
        const documents = res.data as DocumentWithProcessedComment[];
        documents.forEach(doc => {
          doc.truncatedComment = this.getTruncatedComment(doc.comment);
          doc.shouldShowTooltip = this.shouldShowTooltip(doc.comment);
        });
        return {
          data: documents,
          meta: res.meta,
        };
      })
    );
  }

  private shouldShowTooltip(comment: string | null): boolean {
    return comment ? comment.length > this.COMMENT_MAX_LENGTH : false;
  }
}
